// UserContext.js
import React, { createContext, useState, useEffect, useContext, useMemo } from "react";
import {
  setUserCookie,
  // getUserCookie,
  deleteUserCookie,
  // getDomain,
  checkLogoutSignal,
  clearLogoutSignal,
} from "./utils";
export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  // Initialize user from localStorage if it exists, otherwise use empty values
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser
      ? JSON.parse(storedUser)
      : { user_id: "", firstName: "", lastName: "" };
  });

  // Update localStorage whenever the user state changes
  useEffect(() => {
    if (user && user.user_id) {
      localStorage.setItem("user", JSON.stringify(user));
      setUserCookie(user);
    } else {
      localStorage.removeItem("user");
      deleteUserCookie();
    }
  }, [user]);

  // Check for cross-domain logout signals
  useEffect(() => {
    const checkForLogout = () => {
      if (checkLogoutSignal()) {
        console.log("Cross-domain logout detected");

        // Clear logout signal
        clearLogoutSignal();

        // Clear user data
        setUser({ user_id: "", firstName: "", lastName: "" });
        localStorage.removeItem("user");
        deleteUserCookie();

        // Redirect to home page
        window.location.href = "/";
      }
    };

    // Check immediately
    checkForLogout();

    // Set up polling to check for logout signal every 2 seconds
    const interval = setInterval(checkForLogout, 2000);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, []);

  const updateUserFromObject = (newUserData) => {
    setUser((prevUser) => {
      const updatedUser = {
        ...prevUser,
        ...newUserData,
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      return updatedUser;
    });
  };

  return (
    <UserContext.Provider value={{ user, setUser, updateUserFromObject }}>
      {children}
    </UserContext.Provider>
  );
};

// ---------------------------------------------------------
// ✔ Add a clean useUser() hook
// ✔ Add smart derived properties (isLoggedIn, isAdmin, token)
// ---------------------------------------------------------

export const useUser = () => {
  const { user, setUser, updateUserFromObject } = useContext(UserContext);

  // OPTIONALLY derive extra helpers (won't break anything)
  const value = useMemo(() => {
    return {
      user,
      setUser,
      updateUserFromObject,

      // helpful for frontend logic
      isLoggedIn: !!user?.user_id,
      token: user?.access_token || null,      // many API calls need this
      role: user?.user_role || null,
      isAdmin:
        user?.user_role === "ADMIN" || 
        user?.user_role === "admin" ||
        user?.user_role === "SUPER_ADMIN" || 
        user?.user_role === "super_admin",    // adjust once UUIDs are final
    };
  }, [user, setUser, updateUserFromObject]);

  return value;
};
